import { Suspense, lazy, useEffect, useState, type JSX } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { restoreSession, useLogout, useMe } from './features/auth/api';
import { ChangePasswordPage } from './features/auth/ChangePasswordPage';
import { LoginPage } from './features/auth/LoginPage';
import { AgentsPage } from './features/agents/AgentsPage';
import { AuditPage } from './features/audit/AuditPage';
import { EventsPage } from './features/events/EventsPage';
import { NodesPage } from './features/nodes/NodesPage';
import { NotificationsPage } from './features/notifications/NotificationsPage';
import { OverviewPage } from './features/overview/OverviewPage';
import { SitesPage } from './features/sites/SitesPage';
import { UsersPage } from './features/users/UsersPage';
import { CredentialsPage } from './features/vnc/CredentialsPage';
import { SessionsPage } from './features/vnc/SessionsPage';
import { canConnectTo, type NodeVncAccess } from './features/vnc/access';
import type { NodeVideoAccess } from './features/video/access';
import type { SessionTarget } from './shared/session/tabs';
import { usePermittedNodes } from './features/vnc/api';
import { useSshPermittedNodes } from './features/ssh/api';
import { canOpenTerminal } from './features/ssh/access';
import type { NodeSshAccess } from './features/ssh/node-access';
import { useTranslation } from './shared/i18n';
import { authStore } from './shared/api/auth-store';
import { AppShell } from './shared/ui/AppShell';
import { resolveSection } from './shared/ui/navigation';
import { readSection, writeSection } from './shared/ui/shellPreferences';
import { Loading } from './shared/ui/Panel';

/**
 * Карта грузится отдельным куском.
 *
 * React Flow со всем, что к нему прилагается, — половина бандла, и платить
 * за неё на экране входа незачем: до карты доходят не всегда, а логин видят
 * все и каждый раз. §13 отводит на первую отрисовку карты две секунды,
 * и лишние сотни килобайт в общем чанке идут прямо в этот бюджет.
 */
const MapPage = lazy(async () => ({ default: (await import('./features/map/MapPage')).MapPage }));

const PerfProbe = lazy(async () => ({
  default: (await import('./features/map/PerfProbe')).PerfProbe,
}));

/**
 * VNC-консоль грузится отдельным куском.
 *
 * noVNC со всеми декодерами — это сотни килобайт, и платить за них тому,
 * кто открыл карту и ушёл, незачем: до консоли доходят не все и не каждый
 * день, а бандл общий.
 */
const VncPage = lazy(async () => ({ default: (await import('./features/vnc/VncPage')).VncPage }));

/**
 * Терминал грузится отдельным куском по той же причине, что и консоль VNC:
 * xterm.js — это сотня килобайт разбора управляющих последовательностей,
 * и платить за них тому, кто до терминала не дошёл, незачем.
 */
const SshPage = lazy(async () => ({ default: (await import('./features/ssh/SshPage')).SshPage }));

/**
 * Видеостена грузится отдельным куском и, в отличие от консоли, размонтируется
 * при уходе в другой раздел.
 *
 * Разница намеренная: вкладка VNC — это открытая сессия, которую нельзя
 * восстановить одноразовым тикетом, а ячейка видеостены — поток, который
 * платформа обязана гасить, когда его никто не смотрит (§11, фаза 7,
 * задача 6). Оставить стену смонтированной значило бы держать шестнадцать
 * потоков с площадки, пока инженер работает с картой.
 */
const VideoWallPage = lazy(async () => ({
  default: (await import('./features/video/VideoWallPage')).VideoWallPage,
}));

/**
 * Архив грузится отдельным куском и размонтируется вместе с разделом —
 * по той же причине, что и стена: открытая запись это поток с площадки,
 * который платформа обязана гасить, когда его никто не смотрит.
 */
const ArchivePage = lazy(async () => ({
  default: (await import('./features/archive/ArchivePage')).ArchivePage,
}));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Данные мониторинга живут секунды, а не минуты: устаревшее показывать
      // нельзя, но и лишних запросов при переключении вкладок не нужно.
      staleTime: 5_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <Root />
    </QueryClientProvider>
  );
}

function Root(): JSX.Element {
  const { t } = useTranslation();

  const [token, setToken] = useState<string | null>(authStore.token);
  const [restoring, setRestoring] = useState(true);

  /**
   * Раздел, открытый сейчас.
   *
   * Начальное значение берётся из браузера: перезагрузка страницы не должна
   * уводить человека из раздела, в котором он работал. Здесь лежит именно
   * запомненная строка, а не готовый раздел — годен ли он при нынешних
   * правах, известно только после загрузки профиля, и решает это
   * `resolveSection` ниже.
   */
  const [section, setSection] = useState<string | null>(readSection);

  useEffect(() => authStore.subscribe(setToken), []);

  // Access-токен живёт только в памяти (§10.1), поэтому после перезагрузки
  // страницы сессия восстанавливается по refresh-токену из httpOnly-cookie.
  useEffect(() => {
    void restoreSession().finally(() => setRestoring(false));
  }, []);

  const me = useMe(token !== null);
  const logout = useLogout();

  /**
   * Узел, к которому попросили подключиться.
   *
   * Живёт здесь, а не внутри экрана VNC: просьба приходит с карты или
   * с экрана узлов, то есть из соседнего раздела, и переключение раздела —
   * тоже часть этой просьбы.
   */
  const [vncTarget, setSessionTarget] = useState<SessionTarget | null>(null);

  /**
   * Открыта ли VNC-консоль хотя бы раз за эту сессию браузера.
   *
   * После первого открытия экран остаётся смонтированным навсегда, даже
   * когда смотрят на другой раздел: смонтированный экран равен живому
   * соединению, а размонтаж закрыл бы сессии и потребовал бы новых тикетов.
   * До первого открытия он не монтируется вовсе — noVNC со всеми
   * декодерами грузится отдельным куском, и платить за него тому, кто
   * до консоли не дошёл, незачем.
   */
  const [vncOpened, setVncOpened] = useState(false);

  /** Сколько сессий открыто — счётчик у пункта меню. */
  const [vncSessions, setVncSessions] = useState(0);

  /**
   * Узел, на котором попросили открыть терминал.
   *
   * Отдельно от `vncTarget`: разделы разные, и просьба «открой консоль»
   * не должна утаскивать за собой терминал, а «открой терминал» — консоль.
   */
  const [sshTarget, setSshTarget] = useState<SessionTarget | null>(null);

  /** Открыт ли терминал хотя бы раз за эту сессию браузера. */
  const [sshOpened, setSshOpened] = useState(false);

  /** Сколько терминалов открыто — счётчик у пункта меню. */
  const [sshSessions, setSshSessions] = useState(0);

  /** Камера, которую попросили открыть с карты или из панели узла. */
  const [videoTarget, setVideoTarget] = useState<{ cameraId: string } | null>(null);

  /** То же самое, но для архива: раздел другой, просьба такая же. */
  const [archiveTarget, setArchiveTarget] = useState<{ cameraId: string } | null>(null);

  /** Площадка, которую попросили открыть с обзора. */
  const [mapTarget, setMapTarget] = useState<{ siteId: string } | null>(null);

  // Точечные права спрашиваются один раз на сессию: они меняются редко,
  // а нужны на каждой карточке узла.
  const permitted = usePermittedNodes(token !== null);
  const sshPermitted = useSshPermittedNodes(token !== null);

  if (restoring) {
    return <CenteredMessage text={t('common.restoringSession')} />;
  }

  if (token === null) {
    return <LoginPage />;
  }

  if (me.isPending) {
    return <CenteredMessage text={t('common.loadingProfile')} />;
  }

  if (me.error || !me.data) {
    // Профиль не читается при живом токене — данные могли устареть;
    // самый честный выход отсюда — начать сессию заново.
    authStore.clear();
    return <LoginPage />;
  }

  if (me.data.user.mustChangePassword) {
    return <ChangePasswordPage onDone={() => authStore.clear()} />;
  }

  const granted = new Set<string>(me.data.permissions);

  /*
    Запомненный раздел проверяется правами: за ночь у человека могли забрать
    доступ к видеостене, а до перезагрузки он на ней и работал. Недоступный
    раздел заменяется картой — молча, потому что объяснять тут нечего:
    пункта в меню он уже не увидит.
  */
  const active = resolveSection(section, granted);

  /** Уход в раздел — с записью: следующая загрузка начнётся отсюда же. */
  const navigate = (id: string): void => {
    setSection(id);
    writeSection(id);
  };

  const vncAccess: NodeVncAccess = {
    canConnect: (nodeId) => canConnectTo(granted, permitted.data?.nodeIds ?? [], nodeId),
    canWrite: granted.has('node:write'),
    canManageCredentials: granted.has('credential:write'),
    onConnect: (node) => {
      setSessionTarget(node);
      setVncOpened(true);
      navigate('vnc');
    },
  };

  const sshAccess: NodeSshAccess = {
    canConnect: (nodeId) => canOpenTerminal(granted, sshPermitted.data?.nodeIds ?? [], nodeId),
    canWrite: granted.has('node:write'),
    canManageCredentials: granted.has('credential:write'),
    onConnect: (node) => {
      setSshTarget(node);
      setSshOpened(true);
      navigate('ssh');
    },
  };

  const videoAccess: NodeVideoAccess = {
    canView: granted.has('camera:view'),
    canWrite: granted.has('node:write'),
    canArchive: granted.has('camera:archive'),
    onOpen: (cameraId) => {
      setVideoTarget({ cameraId });
      navigate('video');
    },
    onOpenArchive: (cameraId) => {
      setArchiveTarget({ cameraId });
      navigate('archive');
    },
  };

  return (
    <AppShell
      me={me.data}
      activeId={active}
      onNavigate={(id) => {
        if (id === 'vnc') {
          setVncOpened(true);
        }
        if (id === 'ssh') {
          setSshOpened(true);
        }
        navigate(id);
      }}
      onLogout={() => logout.mutate()}
      badges={{ vnc: vncSessions, ssh: sshSessions }}
    >
      <Suspense fallback={<Loading text={t('common.loadingSection')} />}>
        {/*
          Консоль живёт рядом с остальными разделами, а не вместо них:
          её прячут стилем, а не размонтируют. Уход на карту не должен
          рвать открытые сессии — тикет одноразовый, и вернуться
          к прежнему подключению было бы уже нельзя.
        */}
        <div className={active === 'vnc' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>
          {/*
            Монтируется и тогда, когда консоль просто восстановлена после
            перезагрузки: `vncOpened` в этот момент ещё false — его ставит
            переход по меню, которого не было.
          */}
          {vncOpened || active === 'vnc' ? (
            <VncPage
              target={vncTarget}
              canWriteFiles={granted.has('vnc:filetransfer:write')}
              onOpened={() => setSessionTarget(null)}
              onSessions={setVncSessions}
            />
          ) : null}
        </div>

        {/*
          Терминалы живут рядом с разделами так же, как консоль: прячутся
          стилем, а не размонтируются. Уход на карту не должен рвать открытые
          сессии — тикет одноразовый, и вернуться к прежнему подключению
          было бы уже нельзя.
        */}
        <div className={active === 'ssh' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>
          {sshOpened || active === 'ssh' ? (
            <SshPage
              target={sshTarget}
              canTrustHostKey={granted.has('credential:write')}
              canReadFiles={granted.has('ssh:sftp:read')}
              canWriteFiles={granted.has('ssh:sftp:write')}
              onOpened={() => setSshTarget(null)}
              onSessions={setSshSessions}
            />
          ) : null}
        </div>

        {active === 'vnc' || active === 'ssh' ? null : active === 'video' ? (
          <VideoWallPage
            target={videoTarget}
            onOpened={() => setVideoTarget(null)}
            canShareLayouts={granted.has('org:admin')}
          />
        ) : active === 'archive' ? (
          <ArchivePage target={archiveTarget} onOpened={() => setArchiveTarget(null)} />
        ) : active === 'users' ? (
          <UsersPage currentUserId={me.data.user.id} />
        ) : active === 'audit' ? (
          <AuditPage />
        ) : active === 'notifications' ? (
          <NotificationsPage />
        ) : active === 'agents' ? (
          <AgentsPage canWrite={granted.has('site:write')} />
        ) : active === 'nodes' ? (
          <NodesPage
            canWrite={granted.has('node:write')}
            vnc={vncAccess}
            ssh={sshAccess}
            video={videoAccess}
          />
        ) : active === 'events' ? (
          <EventsPage />
        ) : active === 'overview' ? (
          <OverviewPage
            canSeeNetwork={granted.has('node:read')}
            canSeePlatform={granted.has('site:read')}
            onOpenSite={(siteId) => {
              setMapTarget({ siteId });
              navigate('map');
            }}
          />
        ) : active === 'map' ? (
          <MapPage
            canWrite={granted.has('node:write')}
            canManageSite={granted.has('site:write')}
            vnc={vncAccess}
            ssh={sshAccess}
            video={videoAccess}
            target={mapTarget}
            onOpened={() => setMapTarget(null)}
            currentUserId={me.data.user.id}
          />
        ) : active === 'sessions' ? (
          <SessionsPage canTerminateOthers={granted.has('org:admin')} />
        ) : active === 'credentials' ? (
          <CredentialsPage />
        ) : active === 'perf' ? (
          <PerfProbe />
        ) : (
          <SitesPage canWrite={granted.has('site:write')} />
        )}
      </Suspense>
    </AppShell>
  );
}

function CenteredMessage({ text }: { text: string }): JSX.Element {
  return (
    <main className="flex min-h-[100dvh] items-center justify-center bg-bg">
      <Loading text={text} />
    </main>
  );
}
